import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Tooltip,
  toast,
} from '@qodo/design-system';
import { normalizeTeamData } from 'pages/Teams/utils';
import { isUserTeamOwnerOfSpecificTeam } from 'utils/user';
import { TIER_LEVELS } from '../../consts/plans';
import { useSelfServeGate } from '../../hooks/billing/useSelfServeGate';
import { useUser } from '../../hooks/useUser';
import { SUBSCRIPTION_STATE } from '../../network/httpClients/BillingClient/api.schema';
import { useUpdateUserMutation } from '../../network/mutations';
import { useBillingInfoQuery } from '../../network/queries/useBillingInfoQuery';
import { useTeamsQuery } from '../../network/queries/useTeamsQuery';
import { Separator } from '../../ui/separator';
import { Switch } from '../../ui/switch';
import { cn } from '../../utils/className';
import { spawn } from '../../utils/translations';
import { CancelSubscriptionDialog } from '../CancelSubscriptionDialog';
import { DeleteMemberDialog } from '../DeleteMemberDialog/DeleteMemberDialog';
import { DeleteTeamDialog } from '../DeleteTeamDialog';
import { DeleteAccountBlockers } from './parts/DeleteAccountBlockers';
import { AccountSettingsDialogProps } from './types';

export const AccountSettingsDialog = (props: AccountSettingsDialogProps) => {
  const { open, onOpenChange } = props;
  const { user, isLoading } = useUser();
  const [isDataUtilizationChecked, setIsDataUtilizationChecked] = useState(
    !(user?.optout ?? false),
  );

  const { mutateAsync: updateUser, isPending: isUpdatePending } =
    useUpdateUserMutation();

  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteTeamDialogOpen, setIsDeleteTeamDialogOpen] = useState(false);

  const spawnDialog = spawn('dialogs.user-settings');
  const spawnDataUtilization = spawn('dialogs.user-settings.data-utilization');
  const spawnToasts = spawn('toasts.account.update-preferences');

  const { isSelfServe, canPurchase } = useSelfServeGate();
  const { data: billingInfo } = useBillingInfoQuery();
  const { data: teams } = useTeamsQuery();

  const isBillingAdmin = isSelfServe && canPurchase;
  const hasActiveSub =
    isBillingAdmin &&
    (billingInfo?.subscriptionState === SUBSCRIPTION_STATE.ACTIVE ||
      billingInfo?.subscriptionState === SUBSCRIPTION_STATE.PAST_DUE ||
      billingInfo?.subscriptionState ===
        SUBSCRIPTION_STATE.SCHEDULED_TO_CHANGE);
  const openTeam = isBillingAdmin
    ? (teams ?? []).find(
        (t) => t.active && isUserTeamOwnerOfSpecificTeam(user, t.id),
      )
    : undefined;
  const isDeleteBlocked = hasActiveSub || !!openTeam;

  const canChangeDataCollection = (user?.tierLevel ?? 0) < TIER_LEVELS.PAID;

  const onSaveClick = async () => {
    if (!canChangeDataCollection) return;

    try {
      await updateUser({
        attributes: { optout: !isDataUtilizationChecked },
        user_id: user?.id ?? '',
      });

      toast.success(spawnToasts('success'));
      // eslint-disable-next-line
    } catch (error) {
      toast.error(spawnToasts('error'));
    }
  };

  const onDeleteAccountClick = () => {
    setIsDeleteUserDialogOpen(true);
  };

  const onCloseModalClick = () => onOpenChange(false);

  if (isLoading || !user) {
    return <></>;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] gap-6">
          <DialogHeader>
            <DialogTitle className="text-h3">
              {spawnDialog('title')}
            </DialogTitle>
          </DialogHeader>

          <Separator isDialog className="bg-purple-muted-600" />

          <div className="flex flex-col gap-2">
            <span className="font-semibold text-base text-purple-muted-200">
              {spawnDataUtilization('title')}
            </span>
            <div className="flex gap-2">
              <Tooltip
                triggerProps={{ asChild: true }}
                content={
                  !canChangeDataCollection
                    ? spawnDataUtilization('tooltip')
                    : undefined
                }
              >
                <Switch
                  checked={isDataUtilizationChecked}
                  disabled={!canChangeDataCollection}
                  onCheckedChange={() =>
                    setIsDataUtilizationChecked(!isDataUtilizationChecked)
                  }
                />
              </Tooltip>
              <div
                className={cn(
                  'text-sm',
                  isDataUtilizationChecked
                    ? 'text-purple-muted-200'
                    : 'text-purple-muted-400',
                )}
              >
                <span className="font-bold">
                  {spawnDataUtilization('help')}{' '}
                </span>
                <span>{spawnDataUtilization('description')}</span>
              </div>
            </div>
          </div>

          <div className="flex px-4 py-3 border border-purple-muted-600 rounded-md flex-col gap-2">
            <span className="text-base font-semibold text-purple-muted-200">
              {spawnDialog('delete-account.title')}
            </span>
            <span className="text-sm font-normal text-purple-muted-400">
              {spawnDialog('delete-account.description')}
            </span>
            {isDeleteBlocked && (
              <DeleteAccountBlockers
                hasActiveSub={hasActiveSub}
                blockedTeamName={openTeam?.name}
                onCancelSubscription={() => setIsCancelDialogOpen(true)}
                onDeleteTeam={() => setIsDeleteTeamDialogOpen(true)}
              />
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={onDeleteAccountClick}
              disabled={isDeleteBlocked}
              className="w-fit"
            >
              {spawnDialog('delete-account.title')}
            </Button>
          </div>
          <Separator isDialog className="bg-purple-muted-600" />

          <DialogFooter className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCloseModalClick}
              disabled={isUpdatePending}
            >
              {spawnDialog('action.cancel')}
            </Button>

            {canChangeDataCollection && (
              <Button
                type="button"
                onClick={onSaveClick}
                disabled={isUpdatePending}
                loading={isUpdatePending}
              >
                {spawnDialog('action.save')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isDeleteUserDialogOpen && (
        <DeleteMemberDialog
          open={isDeleteUserDialogOpen}
          onOpenChange={setIsDeleteUserDialogOpen}
          target="self"
        />
      )}

      <CancelSubscriptionDialog
        open={isCancelDialogOpen}
        onOpenChange={setIsCancelDialogOpen}
      />

      {openTeam && (
        <DeleteTeamDialog
          open={isDeleteTeamDialogOpen}
          onOpenChange={setIsDeleteTeamDialogOpen}
          team={normalizeTeamData(openTeam)}
        />
      )}
    </>
  );
};
